import * as React from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { ExcelRangeData, mapExcelStylesToCSS, parseRangeAddress } from "../../services/excel";

interface SpreadsheetRendererProps {
  data: ExcelRangeData;
}

const useStyles = makeStyles({
  tableWrapper: {
    overflowX: "auto",
    overflowY: "auto",
    maxWidth: "100%",
    maxHeight: "450px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: "4px",
    backgroundColor: tokens.colorNeutralBackground1,
    // Sleek custom scrollbars to enhance premium visual design
    "&::-webkit-scrollbar": {
      height: "8px",
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: tokens.colorNeutralBackground1,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: tokens.colorNeutralStroke2,
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: tokens.colorNeutralStroke1,
    }
  },
  table: {
    borderCollapse: "collapse",
    width: "max-content",
    minWidth: "100%",
    fontFamily: "Calibri, sans-serif",
    fontSize: "12px",
    tableLayout: "auto",
  },
  excelTableMode: {
    // Basic Excel-like table styling
    border: `1px solid ${tokens.colorBrandBackground}`,
    "& th": {
      backgroundColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
      fontWeight: "bold",
    },
    "& tr:nth-child(even)": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    }
  },
  cell: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: "6px 12px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: "100px",
    maxWidth: "250px",
  }
});

export const SpreadsheetRenderer: React.FC<SpreadsheetRendererProps> = ({ data }) => {
  const styles = useStyles();

  if (!data || !data.text || data.text.length === 0) {
    return <div>No data available to render.</div>;
  }

  // Pre-process merged areas to determine rowSpan/colSpan and hidden cells
  const mergedSpans = new Map<string, { rowSpan: number; colSpan: number }>();
  const hiddenCells = new Set<string>();

  // Determine the baseline range for offsets
  const baseRange = parseRangeAddress(data.address);

  if (data.mergedAreas) {
    data.mergedAreas.forEach(area => {
      const parsed = parseRangeAddress(area);
      // Map global address back to 0-indexed relative to our data 2D array
      const relStartRow = parsed.startRow - baseRange.startRow;
      const relStartCol = parsed.startCol - baseRange.startCol;
      const relEndRow = parsed.endRow - baseRange.startRow;
      const relEndCol = parsed.endCol - baseRange.startCol;

      if (relStartRow >= 0 && relStartCol >= 0) {
        const rowSpan = relEndRow - relStartRow + 1;
        const colSpan = relEndCol - relStartCol + 1;
        mergedSpans.set(`${relStartRow},${relStartCol}`, { rowSpan, colSpan });

        // Mark all other cells in the merged area as hidden
        for (let r = relStartRow; r <= relEndRow; r++) {
          for (let c = relStartCol; c <= relEndCol; c++) {
            if (r !== relStartRow || c !== relStartCol) {
              hiddenCells.add(`${r},${c}`);
            }
          }
        }
      }
    });
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={`${styles.table} ${data.isTable ? styles.excelTableMode : ''}`}>
        <tbody>
          {data.text.map((row, rowIndex) => {
            const isHeader = data.isTable && rowIndex === 0;
            return (
              <tr key={`row-${rowIndex}`}>
                {row.map((cellText, colIndex) => {
                  const cellKey = `${rowIndex},${colIndex}`;
                  
                  // Skip rendering if cell is hidden by a merge
                  if (hiddenCells.has(cellKey)) return null;

                  const spanInfo = mergedSpans.get(cellKey);
                  const rowSpan = spanInfo ? spanInfo.rowSpan : 1;
                  const colSpan = spanInfo ? spanInfo.colSpan : 1;
                  
                  // Always display the 'text' property (calculated result), never raw formulas
                  const displayValue = cellText;
                  
                  // Apply dynamic styles from Excel mapping
                  const dynamicStyles = mapExcelStylesToCSS(rowIndex, colIndex, data);

                  const CellTag = isHeader ? 'th' : 'td';

                  return (
                    <CellTag 
                      key={`cell-${cellKey}`} 
                      className={styles.cell}
                      style={dynamicStyles}
                      title={displayValue} // Tooltip for overflow
                      rowSpan={rowSpan > 1 ? rowSpan : undefined}
                      colSpan={colSpan > 1 ? colSpan : undefined}
                    >
                      {displayValue}
                    </CellTag>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
